import { FormEvent, useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { apiFetch } from '@/lib/api'
import { scrollToFirstError } from '@/lib/form'
import { DateTimePicker } from '@/components/ui/DateTimePicker'
import { AddressAutocomplete } from '@/components/ui/AddressAutocomplete'
import { useAuth } from '@/lib/hooks/useAuth'

const DURATION_OPTIONS = [
  { label: '30 min', value: 30 },
  { label: '1 heure', value: 60 },
  { label: '2 heures', value: 120 },
  { label: '3 heures', value: 180 },
  { label: '4 heures', value: 240 },
  { label: '6 heures', value: 360 },
  { label: '8 heures (journée)', value: 480 },
]

interface TargetExpert {
  id: string
  firstName: string
  lastInitial: string
  avatarUrl: string | null
  rating: number
  ratingCount: number
  isVerified: boolean
}

interface FieldErrors {
  title?: string
  description?: string
  type?: string
  address?: string
  city?: string
  postalCode?: string
  date?: string
  duration?: string
  amount?: string
}



function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="glass-card p-6">
      <h2 className="text-sm font-semibold text-text-primary mb-5 uppercase tracking-wider text-[11px] opacity-60">
        {title}
      </h2>
      <div className="space-y-4">{children}</div>
    </div>
  )
}

export default function NouvelleMissionPage() {
  const navigate = useNavigate()
  useAuth('COMPANY')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [customDuration, setCustomDuration] = useState(false)
  const [selectedSpecialties, setSelectedSpecialties] = useState<Record<string, number>>({})
  const [isCnpas, setIsCnpas] = useState(false)
  const [missionType, setMissionType] = useState('')
  const [targetExpert, setTargetExpert] = useState<TargetExpert | null>(null)
  const [specialtyConfigs, setSpecialtyConfigs] = useState<{ specialty: string; label: string; shortLabel: string; icon: string; description: string; category: string; active: boolean }[]>([])

  useEffect(() => {
    apiFetch<{ isCnpas: boolean }>('/companies/me')
      .then((d) => setIsCnpas(d.isCnpas ?? false))
      .catch(() => {})
    apiFetch<{ specialty: string; label: string; shortLabel: string; icon: string; description: string; category: string; active: boolean }[]>('/public/specialty-config')
      .then(setSpecialtyConfigs)
      .catch(() => {})
    // Arrivée depuis « Trouver un expert » : spécialité pré-cochée, et prestataire ciblé (notifié en premier)
    const params = new URLSearchParams(window.location.search)
    const preset = params.get('specialty')
    if (preset && /^[A-Z0-9_]+$/.test(preset)) setSelectedSpecialties({ [preset]: 1 })
    const provider = params.get('provider')
    if (provider && /^[\w-]+$/.test(provider)) {
      apiFetch<TargetExpert>(`/public/experts/${provider}`)
        .then((e) => setTargetExpert(e))
        .catch(() => setTargetExpert(null))
    }
  }, [])

  function toggleSpecialty(value: string) {
    setSelectedSpecialties((prev) => {
      if (prev[value]) {
        const next = { ...prev }
        delete next[value]
        return next
      }
      return { ...prev, [value]: 1 }
    })
  }

  function setQuantity(value: string, qty: number) {
    setSelectedSpecialties((prev) => ({ ...prev, [value]: Math.max(1, qty) }))
  }

  function validate(fd: FormData): FieldErrors {
    const errs: FieldErrors = {}
    const title = fd.get('title') as string
    const description = fd.get('description') as string
    const type = fd.get('type') as string
    const address = fd.get('address') as string
    const city = fd.get('city') as string
    const postalCode = fd.get('postalCode') as string
    const date = fd.get('date') as string
    const amount = Number(fd.get('amount'))

    if (!title || title.trim().length < 3) errs.title = 'Titre requis (min. 3 caractères)'
    if (!description || description.trim().length < 10) errs.description = 'Description requise (min. 10 caractères)'
    if (!type) errs.type = 'Type requis'
    if (!address || address.trim().length < 5) errs.address = 'Adresse requise'
    if (!city || city.trim().length < 2) errs.city = 'Ville requise'
    if (!postalCode || !/^\d{5}$/.test(postalCode)) errs.postalCode = 'Code postal invalide (5 chiffres)'
    if (!date) errs.date = 'Date requise'
    else if (new Date(date) < new Date()) errs.date = 'La date doit être dans le futur'
    if (!amount || amount < 1) errs.amount = 'Budget requis (min. 1 €)'

    return errs
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError('')

    const fd = new FormData(e.currentTarget)
    const errs = validate(fd)

    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs)
      scrollToFirstError()
      return
    }
    setFieldErrors({})
    setLoading(true)

    const duration = customDuration
      ? Math.round(Number(fd.get('customDuration')) * 60)
      : Number(fd.get('duration'))

    try {
      const mission = await apiFetch<{ id: string }>('/missions', {
        method: 'POST',
        body: JSON.stringify({
          title: fd.get('title'),
          description: fd.get('description'),
          type: fd.get('type'),
          address: fd.get('address'),
          city: fd.get('city'),
          postalCode: fd.get('postalCode'),
          date: new Date(fd.get('date') as string).toISOString(),
          duration,
          amount: Math.round(Number(fd.get('amount'))),
          radiusKm: Number(fd.get('radiusKm')) || 30,
          deadline: fd.get('deadline') ? new Date(fd.get('deadline') as string).toISOString() : undefined,
          minParticipants: fd.get('minParticipants') ? Number(fd.get('minParticipants')) : undefined,
          requiredSpecialties: Object.keys(selectedSpecialties).length > 0
            ? Object.entries(selectedSpecialties).map(([specialty, quantity]) => ({ specialty, quantity }))
            : undefined,
          targetProviderId: targetExpert?.id,
        }),
      })
      navigate(`/missions/${mission.id}`)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erreur inattendue')
      setLoading(false)
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Link to="/missions" className="text-text-muted hover:text-text-secondary transition-colors text-sm">
          ← Retour
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-text-primary">Nouvelle mission</h1>
          <p className="text-text-secondary text-sm mt-1">
            {targetExpert
              ? `${targetExpert.firstName} ${targetExpert.lastInitial} sera notifié(e) en premier, puis les prestataires à proximité`
              : 'Les prestataires à proximité seront notifiés dès publication'}
          </p>
        </div>
      </div>

      {targetExpert && (
        <div className="mb-6 glass-card p-4 flex items-center gap-3 border-accent/25 bg-accent/[0.04]">
          <div className="w-10 h-10 rounded-xl bg-white/[0.08] border border-white/[0.12] overflow-hidden flex items-center justify-center text-text-secondary font-bold text-sm flex-shrink-0">
            {targetExpert.avatarUrl
              ? <img src={`${import.meta.env.VITE_API_URL ?? 'http://localhost:3001'}${targetExpert.avatarUrl}`} alt="" className="w-full h-full object-cover" />
              : `${targetExpert.firstName[0] ?? ''}${targetExpert.lastInitial[0] ?? ''}`.toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-text-primary text-sm font-semibold">
              Mission proposée à {targetExpert.firstName} {targetExpert.lastInitial}
              {targetExpert.isVerified && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-success/15 border border-success/30 text-success">✓ Vérifié</span>}
            </p>
            <p className="text-text-muted text-xs">
              Réservée pour {targetExpert.firstName} pendant 15 minutes après publication. Sans réponse, elle est proposée aux autres prestataires qualifiés à proximité.
            </p>
          </div>
          <button type="button" onClick={() => setTargetExpert(null)} className="text-text-muted hover:text-text-primary text-xs flex-shrink-0" title="Diffuser à tous directement">✕</button>
        </div>
      )}

      {error && (
        <div className="mb-6 px-4 py-3 rounded-xl bg-danger/10 border border-danger/25 text-danger text-sm">
          {error}
        </div>
      )}

      <form onSubmit={onSubmit} noValidate>
        <div className="space-y-4">

          {/* Section 1 — Informations */}
          <Section title="Informations de la mission">
            <div>
              <label htmlFor="title" className="glass-label">
                Titre de la mission <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                id="title"
                name="title"
                placeholder="Ex : Animation atelier Excel — 20 personnes"
                className={`glass-input${fieldErrors.title ? ' error' : ''}`}
              />
              {fieldErrors.title && <p className="glass-field-error">{fieldErrors.title}</p>}
            </div>

            <div>
              <label htmlFor="description" className="glass-label">
                Description <span className="text-danger">*</span>
              </label>
              <textarea
                id="description"
                name="description"
                rows={4}
                placeholder="Décrivez la mission en détail : objectif, public cible, prérequis, matériel fourni…"
                className={`glass-textarea${fieldErrors.description ? ' error' : ''}`}
              />
              {fieldErrors.description && <p className="glass-field-error">{fieldErrors.description}</p>}
            </div>

            <div>
              <label htmlFor="type" className="glass-label">
                Type de mission <span className="text-danger">*</span>
              </label>
              <select
                id="type"
                name="type"
                className={`glass-input${fieldErrors.type ? ' error' : ''}`}
                defaultValue=""
                onChange={(e) => setMissionType(e.target.value)}
              >
                <option value="" disabled>Sélectionner un type…</option>
                <option value="EVENT">Événement</option>
                <option value="TRAINING">Formation</option>
                <option value="OTHER">Autre</option>
                {isCnpas && (
                  <>
                    <option value="ADS">🛡️ Agent de sécurité (ADS)</option>
                    <option value="SSIAP">🛡️ SSIAP</option>
                  </>
                )}
              </select>
              {fieldErrors.type && <p className="glass-field-error">{fieldErrors.type}</p>}
            </div>

            {missionType === 'TRAINING' && (
              <div>
                <label htmlFor="minParticipants" className="glass-label">
                  Nombre minimum de participants{' '}
                  <span className="text-text-muted font-normal">(optionnel)</span>
                </label>
                <input
                  id="minParticipants"
                  type="number"
                  name="minParticipants"
                  min={1}
                  max={10000}
                  placeholder="Ex : 10"
                  className="glass-input"
                />
              </div>
            )}
          </Section>

          {/* Section 2 — Profil recherché */}
          <Section title="Profil recherché">
            <div>
              <label className="glass-label">Types de prestataires requis</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {specialtyConfigs.filter((s) => {
                  if (!s.active) return false
                  if (!missionType) return true
                  if (s.category === 'BOTH') return true
                  if (missionType === 'TRAINING') return s.category === 'TRAINING'
                  if (missionType === 'EVENT') return s.category === 'EVENT'
                  return true
                }).map((s) => {
                  const checked = !!selectedSpecialties[s.specialty]
                  return (
                    <div
                      key={s.specialty}
                      className={[
                        'flex items-center gap-3 p-3 rounded-xl border transition-all',
                        checked ? 'bg-accent/10 border-accent/30' : 'border-white/[0.08] hover:border-white/[0.15]',
                      ].join(' ')}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleSpecialty(s.specialty)}
                        className="accent-[#6E7FFF] w-4 h-4 flex-shrink-0 cursor-pointer"
                      />
                      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => toggleSpecialty(s.specialty)}>
                        <div className="flex items-center gap-1.5">
                          <span>{s.icon || '🔧'}</span>
                          <span className="text-sm font-medium text-text-primary">{s.shortLabel || s.label || s.specialty}</span>
                        </div>
                        {s.description && (
                          <p className="text-text-muted text-xs mt-0.5 leading-tight line-clamp-1">{s.description}</p>
                        )}
                      </div>
                      {checked && (
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => setQuantity(s.specialty, (selectedSpecialties[s.specialty] ?? 1) - 1)}
                            className="w-6 h-6 rounded-lg bg-white/[0.08] hover:bg-white/[0.15] text-text-primary text-sm flex items-center justify-center transition-colors"
                          >−</button>
                          <span className="text-sm font-semibold text-text-primary w-5 text-center">
                            {selectedSpecialties[s.specialty]}
                          </span>
                          <button
                            type="button"
                            onClick={() => setQuantity(s.specialty, (selectedSpecialties[s.specialty] ?? 1) + 1)}
                            className="w-6 h-6 rounded-lg bg-white/[0.08] hover:bg-white/[0.15] text-text-primary text-sm flex items-center justify-center transition-colors"
                          >+</button>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
              {Object.keys(selectedSpecialties).length === 0 && (
                <p className="text-text-muted text-xs mt-2">Aucune spécialité sélectionnée — tous les prestataires seront notifiés.</p>
              )}
              {Object.keys(selectedSpecialties).length > 0 && (
                <p className="text-text-muted text-xs mt-2">
                  Seuls les prestataires ayant au moins une de ces spécialités recevront la notification.
                </p>
              )}
            </div>
          </Section>

          {/* Section 3 — Lieu */}
          <Section title="Lieu de la mission">
            <AddressAutocomplete required fieldErrors={fieldErrors} />

            <div>
              <label htmlFor="radiusKm" className="glass-label">
                Rayon de recherche des prestataires{' '}
                <span className="text-text-muted font-normal">(km, défaut : 30)</span>
              </label>
              <input
                type="number"
                id="radiusKm"
                name="radiusKm"
                defaultValue={30}
                min={1}
                max={200}
                className="glass-input"
              />
            </div>
          </Section>

          {/* Section 4 — Date & Durée */}
          <Section title="Date et durée">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="date" className="glass-label">
                  Date et heure <span className="text-danger">*</span>
                </label>
                <DateTimePicker name="date" required placeholder="Date de la mission…" />
                {fieldErrors.date && <p className="glass-field-error">{fieldErrors.date}</p>}
              </div>
              <div>
                <label htmlFor="deadline" className="glass-label">
                  Date limite de candidature{' '}
                  <span className="text-text-muted font-normal">(optionnel)</span>
                </label>
                <DateTimePicker name="deadline" placeholder="Aucune limite…" align="right" />
              </div>
            </div>

            <div>
              <label className="glass-label">
                Durée <span className="text-danger">*</span>
              </label>
              {!customDuration ? (
                <div className="flex items-center gap-2 flex-wrap">
                  <select name="duration" className="glass-input flex-1" defaultValue={120}>
                    {DURATION_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setCustomDuration(true)}
                    className="glass-btn px-3 py-2.5 text-xs flex-shrink-0"
                  >
                    Personnalisé
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    name="customDuration"
                    min={0.25}
                    max={24}
                    step={0.25}
                    defaultValue={2}
                    placeholder="2"
                    className="glass-input"
                  />
                  <span className="text-text-secondary text-sm flex-shrink-0">heures</span>
                  <button
                    type="button"
                    onClick={() => setCustomDuration(false)}
                    className="glass-btn px-3 py-2.5 text-xs flex-shrink-0"
                  >
                    ← Liste
                  </button>
                </div>
              )}
              {fieldErrors.duration && <p className="glass-field-error">{fieldErrors.duration}</p>}
            </div>
          </Section>

          {/* Section 5 — Budget */}
          <Section title="Budget">
            <div>
              <label htmlFor="amount" className="glass-label">
                Montant proposé (€) <span className="text-danger">*</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  id="amount"
                  name="amount"
                  min={1}
                  step={1}
                  placeholder="180"
                  className={`glass-input pr-8${fieldErrors.amount ? ' error' : ''}`}
                />
                <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-text-muted text-sm">€</span>
              </div>
              <p className="text-text-muted text-xs mt-1.5">
                Le paiement est sécurisé et libéré après validation de la mission.
              </p>
              {fieldErrors.amount && <p className="glass-field-error">{fieldErrors.amount}</p>}
            </div>
          </Section>

          {/* Submit */}
          <div className="flex items-center gap-4 pt-2">
            <button
              type="submit"
              disabled={loading}
              className="glass-btn-accent px-8 py-3 text-sm rounded-xl"
            >
              {loading ? 'Publication en cours…' : 'Publier la mission →'}
            </button>
            <Link to="/missions" className="glass-btn px-6 py-3 text-sm rounded-xl">
              Annuler
            </Link>
          </div>

        </div>
      </form>
    </div>
  )
}
